import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Navbar, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, BookOpen, Users, FileText, Package,
  ChevronRight, Clock, CheckCircle, Sparkles,
  Trash2, AlertTriangle, Filter, Briefcase, AlertCircle,
  ArrowRight, Receipt, Download, Eye, User, Save, Loader2
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PhonePrefixSelect from '../components/PhonePrefixSelect';
import WelcomeNewUserModal from '../components/WelcomeNewUserModal';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIVE_STATUSES = ['collecting', 'generating', 'alta', 'recopilando', 'generando', 'en_revision'];
const CLOSED_STATUSES = ['ready', 'ordered', 'shipped', 'recibido_cliente', 'envio_cliente'];

const EMPLOYEE_PHASES = [
  { key: 'alta', color: 'bg-slate-100 text-slate-700' },
  { key: 'recopilando', color: 'bg-blue-100 text-blue-700' },
  { key: 'generando', color: 'bg-amber-100 text-amber-700' },
  { key: 'en_revision', color: 'bg-orange-100 text-orange-700' },
  { key: 'preparacion_imprenta', color: 'bg-teal-100 text-teal-700' },
  { key: 'envio_imprenta', color: 'bg-purple-100 text-purple-700' },
  { key: 'envio_cliente', color: 'bg-cyan-100 text-cyan-700' },
  { key: 'recibido_cliente', color: 'bg-green-100 text-green-700' },
];

const CLIENT_PHASES = [
  { key: 'alta', color: '#94a3b8' },
  { key: 'recopilando', color: '#3b82f6' },
  { key: 'generando', color: '#f59e0b' },
  { key: 'en_revision', color: '#f97316' },
  { key: 'preparacion_imprenta', color: '#14b8a6' },
  { key: 'envio_imprenta', color: '#a855f7' },
  { key: 'envio_cliente', color: '#06b6d4' },
  { key: 'recibido_cliente', color: '#22c55e' },
];

const statusConfig = {
  collecting: { color: 'bg-blue-100 text-blue-700', icon: Users },
  generating: { color: 'bg-yellow-100 text-yellow-700', icon: Sparkles },
  ready: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
  ordered: { color: 'bg-purple-100 text-purple-700', icon: Package },
  shipped: { color: 'bg-indigo-100 text-indigo-700', icon: Package },
};

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookFilter, setBookFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  const [profile, setProfile] = useState({ first_name: '', last_name: '', email: '', phone: '', phone_prefix: '+34', phone_number: '' });
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [welcomeUserName, setWelcomeUserName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const PhaseProgressBar = ({ currentStatus, pageCount }) => {
    const currentIndex = CLIENT_PHASES.findIndex(p => p.key === currentStatus);
    
    return (
      <div className="space-y-3 mt-2" data-testid="phase-progress-bar">
        {/* Row 1: Phase tracking */}
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Fase del libro</p>
          <div className="flex items-center gap-0.5 w-full">
            {CLIENT_PHASES.map((phase, idx) => {
              const isCompleted = idx < currentIndex;
              const isCurrent = idx === currentIndex;
              return (
                <div key={phase.key} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="h-2 w-full rounded-full transition-all duration-300"
                    style={{
                      backgroundColor: isCompleted || isCurrent ? phase.color : '#e5e7eb',
                      opacity: isCurrent ? 1 : isCompleted ? 0.7 : 0.3,
                    }}
                  />
                  <span className={`text-[9px] leading-tight text-center ${isCurrent ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                    {t(`admin.bookStatus.${phase.key}`, phase.key)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        {/* Row 2: Page tracking */}
        {pageCount != null && (
          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Paginas del libro</p>
              <span className="text-xs font-medium">{pageCount}/240 <span className="text-muted-foreground">({Math.round((pageCount / 240) * 100)}%)</span></span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${pageCount >= 200 ? 'bg-green-500' : pageCount >= 120 ? 'bg-amber-500' : 'bg-red-400'}`}
                style={{ width: `${Math.min(100, (pageCount / 240) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };
  const [bookToDelete, setBookToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [employeeTasks, setEmployeeTasks] = useState(null);
  const [assignedTasks, setAssignedTasks] = useState([]);

  const isEmployee = user?.role === 'employee';

  const fetchData = async () => {
    try {
      const promises = [
        axios.get(`${API_URL}/api/books`, { withCredentials: true }),
        axios.get(`${API_URL}/api/orders`, { withCredentials: true }),
        axios.get(`${API_URL}/api/billing/invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/auth/me`, { withCredentials: true }),
      ];
      if (isEmployee) {
        promises.push(axios.get(`${API_URL}/api/employee/tasks`, { withCredentials: true }));
        promises.push(axios.get(`${API_URL}/api/my-tasks`, { withCredentials: true }));
      }
      const results = await Promise.all(promises);
      setBooks(results[0].data);
      setOrders(results[1].data);
      setInvoices(results[2].data || []);
      if (results[3].data) {
        const u = results[3].data;
        const phone = u.phone || '';
        const prefixMatch = phone.match(/^(\+\d{1,3})\s?(.*)$/);
        setProfile({
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          email: u.email || '',
          phone: u.phone || '',
          phone_prefix: prefixMatch ? prefixMatch[1] : '+34',
          phone_number: prefixMatch ? prefixMatch[2] : phone,
        });
        // First-time welcome: show only for clients (not admins/employees), once
        if (!u.welcome_shown && !u.is_admin && (u.role === 'client' || !u.role)) {
          setWelcomeUserName(u.first_name || u.name || '');
          setWelcomeModalOpen(true);
        }
      }
      if (isEmployee && results[4]) {
        setEmployeeTasks(results[4].data);
      }
      if (isEmployee && results[5]) {
        setAssignedTasks(results[5].data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const changeTaskStatus = async (bookId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/employee/books/${bookId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(t('dashboard.statusChanged'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const getNextPhase = (currentPhase) => {
    const idx = EMPLOYEE_PHASES.findIndex(p => p.key === currentPhase);
    if (idx >= 0 && idx < EMPLOYEE_PHASES.length - 1) return EMPLOYEE_PHASES[idx + 1];
    return null;
  };

  const filteredBooks = books.filter(book => {
    if (bookFilter === 'active') return ACTIVE_STATUSES.includes(book.status);
    if (bookFilter === 'closed') return CLOSED_STATUSES.includes(book.status);
    return true;
  });

  const activeCount = books.filter(b => ACTIVE_STATUSES.includes(b.status)).length;
  const closedCount = books.filter(b => CLOSED_STATUSES.includes(b.status)).length;

  const confirmDeleteBook = (e, book) => {
    e.stopPropagation();
    setBookToDelete(book);
    setDeleteDialogOpen(true);
  };

  const deleteBook = async () => {
    if (!bookToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/books/${bookToDelete.book_id}`, { withCredentials: true });
      toast.success(t('dashboard.bookDeleted'));
      setDeleteDialogOpen(false);
      setBookToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusConfig = (status) => statusConfig[status] || statusConfig.collecting;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground">
                {t('dashboard.welcome')}, {user?.name?.split(' ')[0]}!
              </h1>
              <p className="text-muted-foreground mt-1">
                {isEmployee 
                  ? (employeeTasks?.active_tasks?.length 
                    ? `${employeeTasks.active_tasks.length} ${t('dashboard.pendingTasks')}`
                    : t('dashboard.noTasksDesc'))
                  : (books.length > 0 
                    ? `${books.length} ${books.length === 1 ? t('dashboard.book') : t('dashboard.books')}`
                    : t('dashboard.noBooksDesc'))}
              </p>
            </div>
            {!isEmployee && (
              <Link to="/books/new">
                <Button className="btn-primary gap-2" data-testid="create-book-btn" data-tour="create-book">
                  <Plus className="w-5 h-5" />
                  {t('dashboard.createNew')}
                </Button>
              </Link>
            )}
          </div>

          {/* Employee Tasks Panel */}
          {isEmployee && employeeTasks && (
            <Card className="card-elevated mb-8 border-l-4 border-l-primary" data-testid="employee-tasks">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  {t('dashboard.myTasks')} ({employeeTasks.active_tasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {employeeTasks.active_tasks.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-400" />
                    <p className="font-medium">{t('dashboard.noTasksDesc')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {employeeTasks.active_tasks.map((task) => {
                      const nextPhase = getNextPhase(task.phase);
                      const currentPhaseConf = EMPLOYEE_PHASES.find(p => p.key === task.phase);
                      return (
                        <div
                          key={`${task.book_id}-${task.phase}`}
                          className={`p-4 rounded-lg border ${task.is_delayed ? 'border-red-200 bg-red-50/50' : 'border-green-200 bg-green-50/50'}`}
                          data-testid={`task-${task.book_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${task.is_delayed ? 'bg-red-100' : 'bg-green-100'}`}>
                              {task.is_delayed ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                              ) : (
                                <Clock className="w-5 h-5 text-green-600" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{task.title}</p>
                              <p className="text-xs text-muted-foreground">
                                {task.owner_name} — {task.profile?.recipient_name}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={`${currentPhaseConf?.color || 'bg-gray-100 text-gray-700'} text-xs`}>
                                {t(`admin.bookStatus.${task.phase}`, task.phase)}
                              </Badge>
                              {task.is_delayed ? (
                                <Badge className="bg-red-100 text-red-700 text-xs" data-testid={`task-badge-${task.book_id}`}>
                                  {t('dashboard.delayed')} ({Math.round(task.elapsed_days - task.standard_days)}d)
                                </Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-700 text-xs" data-testid={`task-badge-${task.book_id}`}>
                                  {task.remaining_days > 0 ? `${task.remaining_days}d ${t('dashboard.remaining')}` : t('dashboard.onTrack')}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {/* Status change controls */}
                          <div className="mt-3 flex items-center gap-3 pl-14">
                            <span className="text-xs text-muted-foreground">{t('dashboard.changePhase')}:</span>
                            <Select onValueChange={(val) => changeTaskStatus(task.book_id, val)}>
                              <SelectTrigger className="h-8 w-52" data-testid={`task-status-select-${task.book_id}`}>
                                <SelectValue placeholder={t('dashboard.selectPhase')} />
                              </SelectTrigger>
                              <SelectContent>
                                {EMPLOYEE_PHASES.map(p => (
                                  <SelectItem key={p.key} value={p.key}>
                                    <Badge className={`${p.color} text-xs`}>{t(`admin.bookStatus.${p.key}`)}</Badge>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {nextPhase && (
                              <Button
                                size="sm"
                                className="h-8 gap-1 text-xs"
                                onClick={() => changeTaskStatus(task.book_id, nextPhase.key)}
                                data-testid={`task-next-${task.book_id}`}
                              >
                                {t(`admin.bookStatus.${nextPhase.key}`)} <ArrowRight className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assigned Admin Tasks (for employees) */}
          {isEmployee && assignedTasks.length > 0 && (
            <Card className="card-elevated mb-8 border-l-4 border-l-amber-500" data-testid="assigned-tasks">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500" />
                  {t('dashboard.assignedTasks', 'Tareas asignadas')} ({assignedTasks.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {assignedTasks.map((task) => (
                    <div key={task.task_id} className="flex items-center gap-3 p-3 rounded-lg border bg-amber-50/30" data-testid={`assigned-task-${task.task_id}`}>
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-4 h-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{task.type === 'manual' ? task.title : `${t('admin.taskAssignPhases', 'Asignar responsables')}: ${task.book_title || ''}`}</p>
                        <p className="text-xs text-muted-foreground">
                          {task.created_by_name} — {new Date(task.created_at).toLocaleDateString()}
                        </p>
                        {task.description && <p className="text-xs text-muted-foreground">{task.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Tabs - Only for non-employees */}
          {!isEmployee && (
          <Tabs defaultValue="books" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="books" className="gap-2" data-testid="tab-books">
                <BookOpen className="w-4 h-4" />
                {t('dashboard.myBooks')}
              </TabsTrigger>
              <TabsTrigger value="orders" className="gap-2" data-testid="tab-orders">
                <Package className="w-4 h-4" />
                {t('dashboard.orders')}
              </TabsTrigger>
              <TabsTrigger value="invoices" className="gap-2" data-testid="tab-invoices">
                <Receipt className="w-4 h-4" />
                {t('billing.invoices')}
                {invoices.length > 0 && (
                  <span className="ml-1 text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{invoices.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="profile" className="gap-2" data-testid="tab-profile">
                <User className="w-4 h-4" />
                Mi Perfil
              </TabsTrigger>
            </TabsList>

            {/* Books Tab */}
            <TabsContent value="books">
              {books.length === 0 ? (
                <Card className="card-elevated text-center py-12" data-testid="no-books">
                  <CardContent>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noBooks')}</h3>
                    <p className="text-muted-foreground mb-6">{t('dashboard.noBooksDesc')}</p>
                    <Link to="/books/new">
                      <Button className="btn-primary">{t('dashboard.createNew')}</Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Book Filters */}
                  <div className="flex items-center gap-2 mb-6" data-testid="book-filters">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    <button
                      onClick={() => setBookFilter('all')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        bookFilter === 'all' 
                          ? 'bg-primary text-white' 
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid="filter-all"
                    >
                      {t('dashboard.filterAll')} ({books.length})
                    </button>
                    <button
                      onClick={() => setBookFilter('active')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        bookFilter === 'active' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid="filter-active"
                    >
                      {t('dashboard.filterActive')} ({activeCount})
                    </button>
                    <button
                      onClick={() => setBookFilter('closed')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        bookFilter === 'closed' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                      data-testid="filter-closed"
                    >
                      {t('dashboard.filterClosed')} ({closedCount})
                    </button>
                  </div>

                  {filteredBooks.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground" data-testid="no-filtered-books">
                      <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>{t('dashboard.noFilteredBooks')}</p>
                    </div>
                  ) : (
                    <Card className="card-elevated overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('dashboard.colTitle')}</th>
                              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('dashboard.colRecipient')}</th>
                              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('dashboard.colStatus')}</th>
                              <th className="text-center py-3 px-4 font-medium text-muted-foreground text-sm">{t('bookDetail.collaborators')}</th>
                              <th className="text-center py-3 px-4 font-medium text-muted-foreground text-sm">{t('bookDetail.contributions')}</th>
                              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('dashboard.colDate')}</th>
                              <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">Entrega estimada</th>
                              <th className="text-right py-3 px-4 font-medium text-muted-foreground text-sm">{t('admin.actions')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredBooks.map((book) => {
                              const statusConf = getStatusConfig(book.status);
                              const StatusIcon = statusConf.icon;
                              return (
                                <React.Fragment key={book.book_id}>
                                <tr 
                                  className="border-b-0 hover:bg-muted/50 cursor-pointer group transition-colors"
                                  onClick={() => navigate(`/books/${book.book_id}`)}
                                  data-testid={`book-row-${book.book_id}`}
                                >
                                  <td className="pt-3 px-4">
                                    <span className="font-medium">{book.title}</span>
                                  </td>
                                  <td className="pt-3 px-4 text-sm text-muted-foreground">
                                    {book.profile.recipient_name}
                                    <span className="block text-xs">{t(`profiles.${book.profile.profile_type}`)}</span>
                                  </td>
                                  <td className="pt-3 px-4">
                                    <Badge className={`status-badge ${statusConf.color}`}>
                                      <StatusIcon className="w-3 h-3 mr-1" />
                                      {t(`admin.bookStatus.${book.status}`, book.status)}
                                    </Badge>
                                  </td>
                                  <td className="pt-3 px-4 text-center">{book.contributors_count}</td>
                                  <td className="pt-3 px-4 text-center">{book.contributions_count}</td>
                                  <td className="pt-3 px-4 text-sm text-muted-foreground">
                                    {new Date(book.created_at).toLocaleDateString()}
                                  </td>
                                  <td className="pt-3 px-4 text-sm">
                                    {book.estimated_delivery ? (
                                      <span className="text-primary font-medium">
                                        {new Date(book.estimated_delivery).toLocaleDateString()}
                                      </span>
                                    ) : (
                                      <span className="text-muted-foreground">—</span>
                                    )}
                                  </td>
                                  <td className="pt-3 px-4 text-right">
                                    <button
                                      onClick={(e) => confirmDeleteBook(e, book)}
                                      className="p-2 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100"
                                      data-testid={`delete-book-${book.book_id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                                <tr 
                                  className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                                  onClick={() => navigate(`/books/${book.book_id}`)}
                                >
                                  <td colSpan={8} className="pb-3 px-4 pt-0">
                                    <PhaseProgressBar currentStatus={book.status} pageCount={book.page_count} />
                                  </td>
                                </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  )}
                </>
              )}
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              {orders.length === 0 ? (
                <Card className="card-elevated text-center py-12" data-testid="no-orders">
                  <CardContent>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Package className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-2">{t('dashboard.noOrders')}</h3>
                    <p className="text-muted-foreground">{t('dashboard.noOrdersDesc')}</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <Card 
                      key={order.order_id} 
                      className="card-elevated cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => navigate(`/orders/${order.order_id}`)}
                      data-testid={`order-card-${order.order_id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <h3 className="font-heading font-semibold">{order.book_title}</h3>
                            <p className="text-sm text-muted-foreground">
                              {order.order_id} • {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-semibold">{order.total_amount.toFixed(2)}€</p>
                              <p className="text-sm text-muted-foreground">{order.quantity} {order.quantity === 1 ? t('orderTracking.copy') : t('orderTracking.copies')}</p>
                            </div>
                            <Badge className={`status-badge ${
                              order.status === 'paid' ? 'bg-green-100 text-green-700' :
                              order.status === 'shipped' ? 'bg-indigo-100 text-indigo-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {t(`order.status.${order.status}`)}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices">
              {invoices.length === 0 ? (
                <Card className="card-elevated text-center py-12" data-testid="no-invoices">
                  <CardContent>
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Receipt className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-2">{t('billing.noInvoices')}</h3>
                    <p className="text-muted-foreground">{t('billing.noInvoicesDesc')}</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="card-elevated overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('billing.invoiceNumber')}</th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('billing.invoiceDate')}</th>
                          <th className="text-left py-3 px-4 font-medium text-muted-foreground text-sm">{t('billing.invoiceType')}</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground text-sm">{t('billing.invoiceTotal')}</th>
                          <th className="text-center py-3 px-4 font-medium text-muted-foreground text-sm">{t('billing.invoiceStatus')}</th>
                          <th className="text-right py-3 px-4 font-medium text-muted-foreground text-sm"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((inv) => (
                          <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-muted/50 transition-colors" data-testid={`invoice-row-${inv.invoice_id}`}>
                            <td className="py-3 px-4">
                              <span className="font-medium text-sm">{inv.invoice_number}</span>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {new Date(inv.created_at).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                inv.invoice_type === 'initial' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-purple-100 text-purple-700'
                              }`}>
                                {inv.invoice_type === 'initial' ? t('billing.typeInitial') : t('billing.typeExtras')}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <span className="font-semibold">{inv.total.toFixed(2)}€</span>
                              <span className="block text-[10px] text-muted-foreground">{t('billing.inclTax')}</span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                inv.status === 'paid' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {inv.status === 'paid' ? t('billing.statusPaid') : t('billing.statusPending')}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API_URL}/api/billing/invoices/${inv.invoice_id}/pdf`, {
                                        withCredentials: true, responseType: 'blob'
                                      });
                                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                      setPreviewUrl(url);
                                      setPreviewNumber(inv.invoice_number);
                                    } catch { toast.error('Error al cargar la factura'); }
                                  }}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                                  data-testid={`preview-invoice-${inv.invoice_id}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  Ver
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      const res = await axios.get(`${API_URL}/api/billing/invoices/${inv.invoice_id}/pdf`, {
                                        withCredentials: true, responseType: 'blob'
                                      });
                                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.download = `factura_${inv.invoice_number}.pdf`;
                                      link.click();
                                      window.URL.revokeObjectURL(url);
                                    } catch { toast.error('Error al descargar la factura'); }
                                  }}
                                  className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                                  data-testid={`download-invoice-${inv.invoice_id}`}
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <Card className="card-elevated" data-testid="profile-section">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Datos personales
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-medium">Nombre <span className="text-red-500">*</span></Label>
                      <Input
                        value={profile.first_name}
                        onChange={(e) => setProfile(p => ({ ...p, first_name: e.target.value }))}
                        placeholder="Tu nombre"
                        className="mt-1"
                        data-testid="profile-first-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium">Apellidos <span className="text-red-500">*</span></Label>
                      <Input
                        value={profile.last_name}
                        onChange={(e) => setProfile(p => ({ ...p, last_name: e.target.value }))}
                        placeholder="Tus apellidos"
                        className="mt-1"
                        data-testid="profile-last-name"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Correo electronico</Label>
                    <Input
                      value={profile.email}
                      disabled
                      className="mt-1 bg-muted/50"
                      data-testid="profile-email"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">El email no se puede cambiar</p>
                  </div>

                  <div>
                    <Label className="text-xs font-medium">Telefono</Label>
                    <div className="flex gap-2 mt-1">
                      <PhonePrefixSelect
                        value={profile.phone_prefix}
                        onValueChange={(v) => setProfile(p => ({ ...p, phone_prefix: v }))}
                        testId="profile-phone-prefix"
                      />
                      <Input
                        value={profile.phone_number}
                        onChange={(e) => setProfile(p => ({ ...p, phone_number: e.target.value }))}
                        placeholder="612 345 678"
                        className="flex-1"
                        data-testid="profile-phone"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t">
                    <Button
                      onClick={async () => {
                        if (!profile.first_name.trim() || !profile.last_name.trim()) {
                          toast.error('Nombre y apellidos son obligatorios');
                          return;
                        }
                        setSavingProfile(true);
                        try {
                          const fullPhone = profile.phone_number ? `${profile.phone_prefix} ${profile.phone_number}` : '';
                          await axios.put(`${API_URL}/api/auth/me`, {
                            first_name: profile.first_name.trim(),
                            last_name: profile.last_name.trim(),
                            phone: fullPhone,
                          }, { withCredentials: true });
                          toast.success('Datos actualizados correctamente');
                        } catch {
                          toast.error('Error al guardar los datos');
                        } finally {
                          setSavingProfile(false);
                        }
                      }}
                      disabled={savingProfile}
                      className="gap-2"
                      data-testid="save-profile-btn"
                    >
                      {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {savingProfile ? 'Guardando...' : 'Guardar cambios'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          )}
        </div>
      </main>

      {/* Delete Book Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {t('dashboard.deleteBook')}
            </DialogTitle>
            <DialogDescription>
              {t('dashboard.confirmDeleteBook')} <strong>{bookToDelete?.title}</strong>?
              <br /><br />
              {t('dashboard.deleteBookWarning')}
              <ul className="list-disc list-inside mt-2 text-sm">
                <li>{t('dashboard.deleteBookData')}</li>
                <li>{t('dashboard.deleteBookContributors')}</li>
                <li>{t('dashboard.deleteBookContributions')}</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={deleteBook} 
              disabled={deleting}
              data-testid="confirm-delete-book"
            >
              {deleting ? t('common.loading') : t('dashboard.deleteBook')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => {
        if (!open) {
          if (previewUrl) window.URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
          setPreviewNumber('');
        }
      }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden" data-testid="invoice-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-primary" />
                {t('billing.invoiceNumber')} {previewNumber}
              </DialogTitle>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                if (previewUrl) {
                  const link = document.createElement('a');
                  link.href = previewUrl;
                  link.download = `factura_${previewNumber}.pdf`;
                  link.click();
                }
              }} data-testid="preview-download-btn">
                <Download className="w-3.5 h-3.5" /> {t('billing.downloadPdf')}
              </Button>
            </div>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              className="w-full flex-1"
              style={{ height: 'calc(85vh - 70px)', border: 'none' }}
              title={`Factura ${previewNumber}`}
              data-testid="invoice-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>

      <Footer />
      <WelcomeNewUserModal
        open={welcomeModalOpen}
        onClose={() => setWelcomeModalOpen(false)}
        userName={welcomeUserName}
      />
    </div>
  );
};

export default Dashboard;
